import React, { useState, useEffect } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import HomePage from "./screens/homePage";
import ProductsPage from "./screens/productPage";
import OrdersPage from "./screens/ordersPage";
import CheckoutPage from "./screens/checkoutPage";
import UserPage from "./screens/userPage";
import HomeNavbar from "./components/headers/HomeNavbar";
import OtherNavbar from "./components/headers/OtherNavbar";
import Footer from "./components/footer";
import HelpPage from "./screens/helpPage";
import HelpersPage from "./screens/helpersPage";
import HelperDetail from "./screens/helpersPage/HelperDetail";
import PaymentResultPage from "./screens/paymentPage";
import useBasket from "./components/hooks/useBasket";
import AuthenticationModal from "./components/auth";
import "../css/app.css";
import "../css/navbar.css";
import "../css/footer.css";
import { sweetErrorHandling, sweetTopSuccessAlert } from "../lib/sweetAlert";
import { Messages } from "../lib/config";
import MemberService from "./services/memberService";
import { useGlobals } from "./components/hooks/useGlobals";
import {
  adoptGuestFavorites,
  clearFavorites,
} from "./components/favorite/favStore";

function App() {
  const location = useLocation();

  // Scroll to the top (hero) on every route change so pages open from the top,
  // not wherever the previous page was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const { authMember, setAuthMember } = useGlobals();
  const { cartItems, onAdd, onRemove, onDelete, onDeleteAll } = useBasket();

  // Favorites belong to the account, so the cache follows the session: load it
  // whenever someone is signed in (login *and* page refresh), drop it on the
  // way out. adoptGuestFavorites also hands over anything liked before signing
  // in, and falls through to a plain load when there is nothing pending — so
  // one call covers both entry paths.
  useEffect(() => {
    if (authMember) adoptGuestFavorites();
    else clearFavorites();
  }, [authMember]);
  const [signupOpen, setSignupOpen] = useState<boolean>(false);
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /** HANDLERS **/
  const handleSignupClose = () => setSignupOpen(false);
  const handleLoginClose = () => setLoginOpen(false);

  const handleLogoutClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleCloseLogout = () => setAnchorEl(null);
  const handleLogoutRequest = async () => {
    try {
      const member = new MemberService();
      await member.logout();

      await sweetTopSuccessAlert("success", 700);
      setAuthMember(null);
    } catch (err) {
      console.log(err);
      sweetErrorHandling(Messages.error1);
    }
  };

  return (
    <>
      {location.pathname === "/" ? (
        <HomeNavbar
          cartItems={cartItems}
          onAdd={onAdd}
          onRemove={onRemove}
          onDelete={onDelete}
          onDeleteAll={onDeleteAll}
          setSignupOpen={setSignupOpen}
          setLoginOpen={setLoginOpen}
          anchorEl={anchorEl}
          handleLogoutClick={handleLogoutClick}
          handleCloseLogout={handleCloseLogout}
          handleLogoutRequest={handleLogoutRequest}
        />
      ) : (
        <OtherNavbar
          cartItems={cartItems}
          onAdd={onAdd}
          onRemove={onRemove}
          onDelete={onDelete}
          onDeleteAll={onDeleteAll}
          setSignupOpen={setSignupOpen}
          setLoginOpen={setLoginOpen}
          anchorEl={anchorEl}
          handleLogoutClick={handleLogoutClick}
          handleCloseLogout={handleCloseLogout}
          handleLogoutRequest={handleLogoutRequest}
        />
      )}
      <Switch>
        <Route path="/products">
          <ProductsPage onAdd={onAdd} />
        </Route>
        <Route path="/checkout">
          <CheckoutPage
            cartItems={cartItems}
            onAdd={onAdd}
            onRemove={onRemove}
            onDelete={onDelete}
            onDeleteAll={onDeleteAll}
          />
        </Route>
        <Route path="/orders">
          <OrdersPage />
        </Route>
        <Route path="/payment/:outcome(success|fail)">
          <PaymentResultPage />
        </Route>
        <Route path="/member-page">
          <UserPage />
        </Route>
        <Route path="/helpers/:helperId">
          <HelperDetail />
        </Route>
        <Route path="/helpers">
          <HelpersPage />
        </Route>
        <Route path="/help">
          <HelpPage />
        </Route>
        <Route path="/">
          <HomePage />
          {/* <Test /> */}
        </Route>
      </Switch>
      <Footer />
      <AuthenticationModal
        signupOpen={signupOpen}
        loginOpen={loginOpen}
        handleLoginClose={handleLoginClose}
        handleSignupClose={handleSignupClose}
      />
    </>
  );
}

export default App;
